import { type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Bell,
  Calendar,
  CalendarClock,
  CalendarDays,
  ChevronDown,
  CircleUser,
  Clock,
  ExternalLink,
  FileUp,
  Gauge,
  GitBranch,
  Globe,
  KanbanSquare,
  Mail,
  MailCheck,
  MessageSquare,
  Paintbrush,
  Puzzle,
  RotateCcw,
  Timer,
  Settings2,
  Star,
  Tag,
  Users,
  Webhook,
  Zap,
} from "lucide-react";

import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

/* ------------------------------------------------------------------ */
/*  Settings navigation model                                          */
/* ------------------------------------------------------------------ */

interface SettingsNavItem {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  /**
   * A built-in section of the unified Settings page, selected via the
   * `?tab=` query param (e.g. "general"). Mutually exclusive with `path`.
   */
  tab?: string;
  /**
   * A standalone settings route (e.g. "/admin/webhooks"). Mutually
   * exclusive with `tab`.
   */
  path?: string;
}

interface SettingsNavGroup {
  label: string;
  items: SettingsNavItem[];
}

/**
 * The single source of truth for the Settings left rail. Customer-facing
 * configuration only — operator/dev tooling (CI, platform admin) lives in
 * the Platform nav group, not here.
 */
export const SETTINGS_NAV: SettingsNavGroup[] = [
  {
    label: "Account",
    items: [
      { label: "Profile", icon: CircleUser, tab: "profile" },
      { label: "Notifications", icon: Bell, tab: "notifications" },
    ],
  },
  {
    label: "Workspace",
    items: [
      { label: "General", icon: Settings2, tab: "general" },
      { label: "Team", icon: Users, tab: "users" },
      { label: "Branding", icon: Paintbrush, path: "/admin/branding" },
      { label: "Modules", icon: Puzzle, path: "/admin/modules" },
      { label: "Data import", icon: FileUp, path: "/admin/import" },
    ],
  },
  {
    label: "Sales",
    items: [
      { label: "Pipelines", icon: KanbanSquare, path: "/admin/pipelines" },
      { label: "Lead routing", icon: GitBranch, path: "/admin/lead-routing" },
      { label: "Custom Fields & Tags", icon: Tag, path: "/admin/crm-fields" },
    ],
  },
  {
    label: "Communication",
    items: [
      { label: "Email", icon: Mail, tab: "email" },
      { label: "System Emails", icon: MailCheck, tab: "system-emails" },
      { label: "SMS", icon: MessageSquare, tab: "sms" },
    ],
  },
  {
    label: "Scheduling",
    items: [
      { label: "Bookings", icon: Calendar, tab: "bookings" },
      { label: "Team booking pages", icon: CalendarDays, path: "/admin/org-bookings" },
      { label: "Referral partners", icon: Users, path: "/admin/referral-partners" },
    ],
  },
  {
    label: "Automation",
    items: [
      { label: "Automations", icon: Zap, path: "/admin/automations" },
      { label: "Reorder rules", icon: RotateCcw, path: "/admin/reorder-rules" },
      { label: "Inactivity rules", icon: Clock, path: "/admin/inactivity-rules" },
      { label: "Stage rules", icon: Timer, path: "/admin/stage-rules" },
      { label: "Score threshold rules", icon: Gauge, path: "/admin/score-threshold-rules" },
      { label: "Date rules", icon: CalendarClock, path: "/admin/date-rules" },
    ],
  },
  {
    label: "Developer",
    items: [
      { label: "Integrations", icon: Globe, tab: "integrations" },
      { label: "Webhooks", icon: Webhook, path: "/admin/webhooks" },
      { label: "Client Portal", icon: ExternalLink, path: "/admin/portal" },
    ],
  },
  {
    label: "Reputation",
    items: [{ label: "Reviews", icon: Star, path: "/admin/reviews" }],
  },
];

/** The `to` target a rail item links to. */
function itemTo(item: SettingsNavItem): string {
  if (item.path) return item.path;
  // The unified Settings page treats "general" as the default with no param.
  return item.tab === "general" ? "/settings" : `/settings?tab=${item.tab}`;
}

/** Whether a rail item represents the current location. */
function isItemActive(
  item: SettingsNavItem,
  pathname: string,
  currentTab: string,
): boolean {
  if (item.tab) {
    return pathname === "/settings" && currentTab === item.tab;
  }
  // Standalone route — match the base path or any of its detail sub-routes
  // (e.g. /settings/webhooks/:id keeps "Webhooks" highlighted).
  return pathname === item.path || pathname.startsWith(`${item.path}/`);
}

/* ------------------------------------------------------------------ */
/*  Rail                                                               */
/* ------------------------------------------------------------------ */

function RailLink({
  item,
  active,
}: {
  item: SettingsNavItem;
  active: boolean;
}) {
  const Icon = item.icon;
  return (
    <Link
      to={itemTo(item)}
      aria-current={active ? "page" : undefined}
      className={cn(
        "flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors",
        active
          ? "relative bg-primary/10 font-medium text-primary before:absolute before:left-0 before:top-1/2 before:h-5 before:w-[3px] before:-translate-y-1/2 before:rounded-r-full before:bg-primary"
          : "text-muted-foreground hover:bg-accent/50 hover:text-foreground",
      )}
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span className="truncate">{item.label}</span>
    </Link>
  );
}

/* ------------------------------------------------------------------ */
/*  Layout                                                             */
/* ------------------------------------------------------------------ */

/**
 * Shared chrome for every Settings destination: the app shell plus a
 * grouped left rail that surfaces all customer-facing configuration in one
 * place. Drop-in compatible with {@link AppShell} — any settings page can
 * render `<SettingsLayout>{content}</SettingsLayout>` and inherit the rail.
 */
export function SettingsLayout({ children }: { children: ReactNode }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const currentTab = params.get("tab") ?? "general";
  const navItems = SETTINGS_NAV.flatMap((group) => group.items);
  const activeItem =
    navItems.find((item) => isItemActive(item, location.pathname, currentTab)) ??
    navItems.find((item) => item.tab === "general") ??
    navItems[0];
  const ActiveIcon = activeItem.icon;

  return (
    <AppShell>
      <div className="mx-auto w-full max-w-6xl px-4 py-6 sm:px-6 sm:py-8">
        <header className="mb-6">
          <h1 className="font-display text-3xl leading-tight">Settings</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage your Relay workspace, channels, and team
          </p>
        </header>

        {/* Mobile: explicit dropdown affordance for the full settings menu */}
        <div className="mb-6 md:hidden">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                className="h-11 w-full justify-between gap-3 rounded-lg px-3"
                aria-label="Open settings menu"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <ActiveIcon className="h-4 w-4 shrink-0" />
                  <span className="truncate">{activeItem.label}</span>
                </span>
                <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="max-h-[70vh] w-[calc(100vw-2rem)] overflow-y-auto"
            >
              {SETTINGS_NAV.map((group, groupIndex) => (
                <div key={group.label}>
                  {groupIndex > 0 ? <DropdownMenuSeparator /> : null}
                  <DropdownMenuLabel className="text-xs text-muted-foreground">
                    {group.label}
                  </DropdownMenuLabel>
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    const active = isItemActive(
                      item,
                      location.pathname,
                      currentTab,
                    );
                    return (
                      <DropdownMenuItem key={item.path ?? item.tab} asChild>
                        <Link
                          to={itemTo(item)}
                          aria-current={active ? "page" : undefined}
                          className={cn(
                            "gap-2",
                            active && "font-medium text-primary",
                          )}
                        >
                          <Icon className="h-4 w-4" />
                          <span>{item.label}</span>
                        </Link>
                      </DropdownMenuItem>
                    );
                  })}
                </div>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="md:grid md:grid-cols-[200px_1fr] md:gap-8">
          {/* Desktop: grouped vertical rail */}
          <aside className="hidden md:block">
            <nav aria-label="Settings" className="sticky top-6 space-y-5">
              {SETTINGS_NAV.map((group) => (
                <div key={group.label}>
                  <p className="eyebrow mb-1 px-3">{group.label}</p>
                  <div className="space-y-0.5">
                    {group.items.map((item) => (
                      <RailLink
                        key={item.path ?? item.tab}
                        item={item}
                        active={isItemActive(
                          item,
                          location.pathname,
                          currentTab,
                        )}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </nav>
          </aside>

          {/* Content */}
          <div className="min-w-0">{children}</div>
        </div>
      </div>
    </AppShell>
  );
}
